import { logger } from '../utils/logger';

// Versión actual del esquema de persist
export const PERSIST_VERSION = '1.0.2';

// Umbral de antigüedad en milisegundos (1 hora)
const MAX_AGE_MS = 60 * 60 * 1000; // 1 hora

// Interfaz para las migraciones
export interface Migration {
  version: string;
  migrate: (state: any) => any;
  description: string;
}

// Interfaz para el estado persistido con versión
export interface PersistedState {
  version: string;
  state: any;
  timestamp: string;
}

// Sistema de migración centralizado
class PersistMigrationManager {
  private migrations: Migration[] = [];
  private currentVersion: string;

  constructor() {
    this.currentVersion = PERSIST_VERSION;
    this.registerDefaultMigrations();
  }

  /**
   * Registra una migración
   */
  registerMigration(migration: Migration): void {
    this.migrations.push(migration);
    // Ordenar migraciones por versión
    this.migrations.sort((a, b) => this.compareVersions(a.version, b.version));
  }

  /**
   * Registra las migraciones por defecto
   */
  private registerDefaultMigrations(): void {
    // Migración inicial - v1.0.0
    this.registerMigration({
      version: '1.0.0',
      description: 'Migración inicial - Estructura base de stores',
      migrate: (state: any) => {
        logger.log('PERSIST_MIGRATION', '🔄 Aplicando migración v1.0.0');
        
        // Si no hay estado, retornar estado vacío
        if (!state) return {};
        
        // Migración para globalStore
        if (state['global-store']) {
          const globalState = state['global-store'];
          if (globalState.state) {
            // Asegurar que los campos requeridos existen
            const migratedGlobal = {
              selectedStoreyId: globalState.state.selectedStoreyId || '',
              elevationMeters: typeof globalState.state.elevationMeters === 'number' 
                ? globalState.state.elevationMeters 
                : 0,
              elevationType: globalState.state.elevationType === 'ellipsoidal' 
                ? 'ellipsoidal' 
                : 'ortometric'
            };
            state['global-store'].state = migratedGlobal;
          }
        }

        // Migración para uiStore
        if (state['ui-store']) {
          const uiState = state['ui-store'];
          if (uiState.state) {
            const migratedUI = {
              panels: {
                ifcInput: uiState.state.panels?.ifcInput || false,
                cadasterInput: uiState.state.panels?.cadasterInput || false,
                configuration: uiState.state.panels?.configuration || false,
                drawing: uiState.state.panels?.drawing || false,
                map: uiState.state.panels?.map || false,
                calculations: uiState.state.panels?.calculations || false,
                export: uiState.state.panels?.export || false,
              },
              modals: uiState.state.modals || [],
              activeModalId: uiState.state.activeModalId || null,
              notifications: uiState.state.notifications || [],
              theme: uiState.state.theme === 'dark' ? 'dark' : 'light',
              language: uiState.state.language === 'en' ? 'en' : 'es',
              sidebarCollapsed: !!uiState.state.sidebarCollapsed,
              isLoading: !!uiState.state.isLoading,
              loadingMessage: uiState.state.loadingMessage || '',
            };
            state['ui-store'].state = migratedUI;
          }
        }

        // Migración para otros stores...
        // Aquí se pueden agregar más migraciones según sea necesario

        return state;
      }
    });

    // Ejemplo de migración futura - v1.1.0
    this.registerMigration({
      version: '1.1.0',
      description: 'Migración futura - Nuevas características',
      migrate: (state: any) => {
        logger.log('PERSIST_MIGRATION', '🔄 Aplicando migración v1.1.0');
        
        // Aquí se implementarían las migraciones para v1.1.0
        // Por ejemplo, agregar nuevos campos, cambiar estructuras, etc.
        
        return state;
      }
    });
  }

  /**
   * Compara dos versiones semánticas
   */
  private compareVersions(a: string, b: string): number {
    const aParts = a.split('.').map(Number);
    const bParts = b.split('.').map(Number);
    
    for (let i = 0; i < Math.max(aParts.length, bParts.length); i++) {
      const aPart = aParts[i] || 0;
      const bPart = bParts[i] || 0;
      
      if (aPart < bPart) return -1;
      if (aPart > bPart) return 1;
    }
    
    return 0;
  }

  /**
   * Obtiene las migraciones necesarias para actualizar desde una versión
   */
  private getRequiredMigrations(fromVersion: string): Migration[] {
    return this.migrations.filter(migration => 
      this.compareVersions(migration.version, fromVersion) > 0
    );
  }

  /**
   * Migra el estado desde una versión a la actual
   */
  migrateState(state: any, fromVersion: string): any {
    if (this.compareVersions(fromVersion, this.currentVersion) === 0) {
      return state; // Ya está en la versión actual
    }

    const requiredMigrations = this.getRequiredMigrations(fromVersion);
    
    if (requiredMigrations.length === 0) {
      logger.warn('PERSIST_MIGRATION', `⚠️ No se encontraron migraciones de ${fromVersion} a ${this.currentVersion}`);
      return state;
    }

    logger.log('PERSIST_MIGRATION', `🔄 Migrando estado de v${fromVersion} a v${this.currentVersion}`);
    logger.log('PERSIST_MIGRATION', `📋 Migraciones a aplicar: ${requiredMigrations.length}`);

    let migratedState = state;
    
    for (const migration of requiredMigrations) {
      try {
        logger.log('PERSIST_MIGRATION', `🔄 Aplicando migración v${migration.version}: ${migration.description}`);
        migratedState = migration.migrate(migratedState);
        logger.log('PERSIST_MIGRATION', `✅ Migración v${migration.version} aplicada exitosamente`);
      } catch (error) {
        logger.error('PERSIST_MIGRATION', `❌ Error en migración v${migration.version}:`, error);
        throw new Error(`Error en migración v${migration.version}: ${error}`);
      }
    }

    logger.log('PERSIST_MIGRATION', `🎉 Migración completada: v${fromVersion} → v${this.currentVersion}`);
    return migratedState;
  }

  /**
   * Obtiene la versión actual del esquema
   */
  getCurrentVersion(): string {
    return this.currentVersion;
  }

  /**
   * Verifica si se necesita migración
   */
  needsMigration(fromVersion: string): boolean {
    return this.compareVersions(fromVersion, this.currentVersion) < 0;
  }

  /**
   * Verifica si un timestamp ha expirado (más antiguo que el umbral)
   */
  isExpired(timestamp: string, maxAgeMs: number = MAX_AGE_MS): boolean {
    try {
      const storedDate = new Date(timestamp);
      const now = new Date();
      const ageMs = now.getTime() - storedDate.getTime();
      return ageMs > maxAgeMs;
    } catch (error) {
      logger.warn('PERSIST_MIGRATION', '⚠️ Error al verificar expiración, considerando como expirado:', error);
      return true; // Si hay error, considerar como expirado por seguridad
    }
  }

  /**
   * Verifica si una versión es incompatible (diferente a la actual)
   */
  isVersionIncompatible(version: string): boolean {
    return this.compareVersions(version, this.currentVersion) !== 0;
  }

  /**
   * Limpia el estado persistido (último recurso)
   */
  clearPersistedState(): void {
    try {
      // Limpiar todos los stores conocidos
      const storeKeys = [
        'global-store',
        'ui-store',
        'map-store',
        'drawing-store',
        'ifc-store',
        'cadastral-store',
        'categories-store',
        'fincas-store',
        'elements-store',
        'unified-elements-store'
      ];

      storeKeys.forEach(key => {
        sessionStorage.removeItem(key);
      });

      logger.log('PERSIST_MIGRATION', '🧹 Estado persistido limpiado');
    } catch (error) {
      logger.error('PERSIST_MIGRATION', '❌ Error al limpiar estado persistido:', error);
    }
  }
}

// Instancia singleton del manager de migraciones
export const persistMigrationManager = new PersistMigrationManager();

// Función helper para crear middleware de persist con migración
export function createPersistMiddleware(
  name: string,
  options?: {
    version?: string;
    migrate?: (state: any) => any;
  }
) {
  return {
    name,
    getStorage: () => sessionStorage,
    serialize: (state: any) => {
      const persistedState: PersistedState = {
        version: options?.version || PERSIST_VERSION,
        state,
        timestamp: new Date().toISOString()
      };
      return JSON.stringify(persistedState);
    },
    deserialize: (str: string) => {
      try {
        const parsed = JSON.parse(str);
        
        // Verificar si tiene timestamp (formato nuevo)
        const hasTimestamp = parsed.timestamp && typeof parsed.timestamp === 'string';
        const storedTimestamp = hasTimestamp ? parsed.timestamp : null;
        
        // Verificar si tiene versión
        const hasVersion = parsed.version && typeof parsed.version === 'string';
        const storedVersion = hasVersion ? parsed.version : null;

        // REGLA 1: Si no tiene versión (formato muy antiguo), limpiar
        if (!hasVersion) {
          logger.warn('PERSIST_MIGRATION', `🧹 ${name}: Sin versión detectada, limpiando store (formato incompatible)`);
          sessionStorage.removeItem(name);
          return {};
        }

        // REGLA 2: Si la versión no coincide con la actual, limpiar (bump forzado)
        if (persistMigrationManager.isVersionIncompatible(storedVersion)) {
          logger.warn('PERSIST_MIGRATION', `🧹 ${name}: Versión incompatible (v${storedVersion} vs v${PERSIST_VERSION}), limpiando store`);
          sessionStorage.removeItem(name);
          return {};
        }

        // REGLA 3: Si tiene más de 1 hora de antigüedad, limpiar
        if (storedTimestamp && persistMigrationManager.isExpired(storedTimestamp)) {
          const ageHours = (new Date().getTime() - new Date(storedTimestamp).getTime()) / (1000 * 60 * 60);
          logger.warn('PERSIST_MIGRATION', `🧹 ${name}: Store expirado (${ageHours.toFixed(2)} horas de antigüedad), limpiando`);
          sessionStorage.removeItem(name);
          return {};
        }

        // Si pasa todas las validaciones, retornar el estado
        // El estado puede estar en parsed.state (formato nuevo) o directamente en parsed (formato antiguo)
        const state = parsed.state !== undefined ? parsed.state : parsed;
        
        logger.log('PERSIST_MIGRATION', `✅ ${name}: Store válido (v${storedVersion}, ${storedTimestamp ? 'timestamp OK' : 'sin timestamp'})`);
        return state;
      } catch (error) {
        logger.error('PERSIST_MIGRATION', `❌ Error al deserializar estado de ${name}:`, error);
        // Si hay error al parsear, limpiar el store corrupto
        try {
          sessionStorage.removeItem(name);
          logger.log('PERSIST_MIGRATION', `🧹 ${name}: Store corrupto eliminado`);
        } catch (cleanupError) {
          logger.error('PERSIST_MIGRATION', `❌ Error al limpiar store corrupto ${name}:`, cleanupError);
        }
        return {};
      }
    }
  };
}
